import { useMemo } from 'react';
import {
  Age,
  CombinedFamilyInfo,
  CustomField,
  RoleApprovalStatus,
  RoleRemovalReason,
} from '../GeneratedClient';
import { getFamilyName } from '../Families/FamilyUtils';
import { personNameString } from '../Families/PersonName';
import { useVisibleCommunities } from '../Model/Data';
import { usePolicy } from '../Model/PolicyModel';
import { useVolunteerFamilies } from '../Model/VolunteersModel';
import { getFamilyCounty } from '../Utilities/getFamilyCounty';
import {
  toCustomFieldGridValue,
  type CustomFieldGridValue,
} from '../Generic/customFieldValue';
import {
  buildVolunteerApprovalRolesPresentation,
  type VolunteerApprovalRolesPresentation,
} from './VolunteerApprovalTab/volunteerApprovalRolePresentation';
import type { filterOption } from './VolunteerApprovalTab/filterOption';
import {
  buildVolunteerMissingRequirementGroups,
  completeRequirementFilterValue,
  missingRequirementFilterValue,
  type VolunteerMissingRequirementGroup,
} from './VolunteerApprovalTab/volunteerMissingRequirementsPresentation';
import {
  notAppliedRoleFilterValue,
  roleFilterValues,
} from './roleFilterValues';
import { organizationNamesByFamilyId } from './volunteerOrganizationModel';
import {
  isRoleApprovalStatusVisibleInSummary,
  optedOutRoleStatusValue,
} from './roleApprovalStatusPresentation';
export type VolunteerCustomFieldValue = CustomFieldGridValue;
export type VolunteerBrowserRowV2 = {
  arrangementAssignmentValues: Record<string, 'assigned' | 'unassigned'>;
  county: string | null;
  family: string;
  familyId: string;
  familyCustomFieldValues: Record<string, VolunteerCustomFieldValue>;
  familyLastName: string;
  id: string;
  missingRequirementGroups: VolunteerMissingRequirementGroup[];
  organizationNames: string[];
  primaryContact: string;
  personName: string;
  personAge?: Age;
  rowKind: 'family' | 'adult' | 'child';
  treePath: string[];
  requirementFilterValues: string[];
  roleFilterValues: string[];
  roleStatusValues: Record<string, string[]>;
  roles: VolunteerApprovalRolesPresentation;
  searchableText: string;
  sourceFamily: CombinedFamilyInfo;
  userRoles: string[];
  volunteerFamilyCount: number;
  volunteerCustomFieldValues: Record<string, VolunteerCustomFieldValue>;
};
type VolunteersBrowserViewModel = {
  arrangementTypes: string[];
  familyCustomFields: CustomField[];
  roleNames: string[];
  rows: VolunteerBrowserRowV2[];
  volunteerCustomFields: CustomField[];
};
function primaryContact(family: CombinedFamilyInfo) {
  return family.family?.adults?.find(
    (adult) => adult.item1?.id === family.family?.primaryFamilyContactPersonId
  )?.item1;
}
function valuesByName(
  values: { customFieldName?: string; value?: unknown }[] | undefined,
  fields: CustomField[]
) {
  const fieldTypes = new Map(fields.map((field) => [field.name, field.type]));

  return Object.fromEntries(
    (values ?? []).flatMap((value) => {
      if (!value.customFieldName) return [];

      const customFieldType = fieldTypes.get(value.customFieldName);
      if (typeof customFieldType === 'undefined') return [];

      return [
        [
          value.customFieldName,
          toCustomFieldGridValue(value.value, customFieldType),
        ],
      ];
    })
  ) as Record<string, VolunteerCustomFieldValue>;
}
function roleStatusValues(family: CombinedFamilyInfo, roleNames: string[]) {
  const now = new Date();
  const removals = [
    ...(family.volunteerFamilyInfo?.roleRemovals ?? []),
    ...Object.values(
      family.volunteerFamilyInfo?.individualVolunteers ?? {}
    ).flatMap((volunteer) => volunteer.roleRemovals ?? []),
  ].filter(
    (removal) =>
      removal.reason === RoleRemovalReason.OptOut &&
      (!removal.effectiveUntil || removal.effectiveUntil > now)
  );
  const hasOptOut = (roleName: string) =>
    removals.some(
      (removal) =>
        removal.roleName.trim().replace(/\s+/g, ' ').toLocaleLowerCase() ===
        roleName.trim().replace(/\s+/g, ' ').toLocaleLowerCase()
    );

  return Object.fromEntries(
    roleNames.map((roleName) => [
      roleName,
      Array.from(
        new Set(
          [
            family.volunteerFamilyInfo?.familyRoleApprovals?.[roleName]
              ?.currentStatus,
            ...Object.values(
              family.volunteerFamilyInfo?.individualVolunteers ?? {}
            ).map(
              (volunteer) =>
                volunteer.approvalStatusByRole?.[roleName]?.currentStatus
            ),
          ]
            .filter(
              (status): status is RoleApprovalStatus =>
                status != null &&
                isRoleApprovalStatusVisibleInSummary(status) &&
                !(status === RoleApprovalStatus.Inactive && hasOptOut(roleName))
            )
            .map(String)
            .concat(
              hasOptOut(roleName) ? [optedOutRoleStatusValue] : []
            )
        )
      ),
    ])
  );
}
function assignmentValues(
  family: CombinedFamilyInfo,
  arrangementTypes: string[]
) {
  return Object.fromEntries(
    arrangementTypes.map((arrangementType) => [
      arrangementType,
      family.volunteerFamilyInfo?.assignments?.some(
        (assignment) =>
          assignment.arrangementType === arrangementType &&
          !assignment.endedAtUtc &&
          !assignment.cancelledAtUtc
      )
        ? 'assigned'
        : 'unassigned',
    ])
  ) as Record<string, 'assigned' | 'unassigned'>;
}
function searchText(family: CombinedFamilyInfo) {
  return [
    ...(family.family?.adults ?? []).map(
      (adult) =>
        `${adult.item1?.firstName ?? ''} ${adult.item1?.lastName ?? ''}`
    ),
    ...(family.family?.children ?? []).map(
      (child) => `${child?.firstName ?? ''} ${child?.lastName ?? ''}`
    ),
  ].join('\n');
}

function requirementFilterValues(
  groups: VolunteerMissingRequirementGroup[]
) {
  const requirementNames = groups.flatMap((group) => group.requirements);
  return requirementNames.length
    ? Array.from(
        new Set([missingRequirementFilterValue, ...requirementNames])
      )
    : [completeRequirementFilterValue];
}

export function buildVolunteerBrowserRow(
  family: CombinedFamilyInfo,
  organizationNames: string[],
  familyCustomFields: CustomField[],
  volunteerCustomFields: CustomField[],
  arrangementTypes: string[],
  roleNamesForPresentation: string[]
): VolunteerBrowserRowV2[] {
  const contact = primaryContact(family);
  const roleFilters = roleNamesForPresentation.map(
    (key): filterOption => ({ key, selected: false, value: undefined })
  );
  const missingRequirementGroups = buildVolunteerMissingRequirementGroups(
    family,
    roleFilters
  );
  const familyValues = valuesByName(
    family.family?.completedCustomFields,
    familyCustomFields
  );
  const volunteerValues = valuesByName(
    family.volunteerFamilyInfo?.completedCustomFields,
    volunteerCustomFields
  );
  const familyId = family.family!.id!;
  const familyRow: VolunteerBrowserRowV2 = {
    arrangementAssignmentValues: assignmentValues(family, arrangementTypes),
    county: getFamilyCounty(family),
    family: getFamilyName(family),
    familyId,
    familyCustomFieldValues: Object.fromEntries(
      familyCustomFields.map((field) => [
        field.name,
        familyValues[field.name] ?? null,
      ])
    ),
    familyLastName: contact?.lastName ?? '⚠ MISSING PRIMARY CONTACT',
    id: familyId,
    missingRequirementGroups,
    organizationNames,
    primaryContact: contact ? personNameString(contact) : '',
    personName: '',
    rowKind: 'family',
    treePath: [`family:${familyId}`],
    requirementFilterValues: requirementFilterValues(missingRequirementGroups),
    roleFilterValues: roleFilterValues(family),
    roleStatusValues: roleStatusValues(family, roleNamesForPresentation),
    roles: buildVolunteerApprovalRolesPresentation(family, roleFilters),
    searchableText: searchText(family),
    sourceFamily: family,
    userRoles: Array.from(
      new Set((family.users ?? []).flatMap((user) => user.locationRoles ?? []))
    ),
    volunteerCustomFieldValues: Object.fromEntries(
      volunteerCustomFields.map((field) => [
        field.name,
        volunteerValues[field.name] ?? null,
      ])
    ),
    volunteerFamilyCount: 1,
  };

  const adults = (family.family?.adults ?? []).flatMap((adult) =>
    adult.item1?.id ? [adult.item1] : []
  );
  const children = (family.family?.children ?? []).filter(
    (child) => !!child?.id
  );
  const memberRequirements = (personId: string) => {
    const groups = buildVolunteerMissingRequirementGroups(
      family,
      roleFilters,
      personId
    );
    return {
      missingRequirementGroups: groups,
      requirementFilterValues: requirementFilterValues(groups),
    };
  };
  const memberRows: VolunteerBrowserRowV2[] = [
    ...adults.map((person) => ({
      ...familyRow,
      id: `${familyId}:adult:${person.id}`,
      personName: personNameString(person),
      personAge: person.age,
      rowKind: 'adult' as const,
      treePath: [
        familyRow.treePath[0],
        `adult:${personNameString(person)}:${person.id}`,
      ],
      roles: buildVolunteerApprovalRolesPresentation(
        family,
        roleFilters,
        person.id
      ),
      ...memberRequirements(person.id),
      userRoles: Array.from(
        new Set(
          (family.users ?? [])
            .filter((user) => user.personId === person.id)
            .flatMap((user) => user.locationRoles ?? [])
        )
      ),
      primaryContact: '',
    })),
    ...children.map((person) => ({
      ...familyRow,
      id: `${familyId}:child:${person.id}`,
      personName: personNameString(person),
      personAge: person.age,
      rowKind: 'child' as const,
      treePath: [
        familyRow.treePath[0],
        `child:${personNameString(person)}:${person.id}`,
      ],
      roles: buildVolunteerApprovalRolesPresentation(
        family,
        roleFilters,
        person.id!
      ),
      ...memberRequirements(person.id!),
      userRoles: Array.from(
        new Set(
          (family.users ?? [])
            .filter((user) => user.personId === person.id)
            .flatMap((user) => user.locationRoles ?? [])
        )
      ),
      primaryContact: '',
    })),
  ];

  return [familyRow, ...memberRows];
}

export function useVolunteersBrowserViewModel(): VolunteersBrowserViewModel {
  const families = useVolunteerFamilies();
  const communities = useVisibleCommunities();
  const policy = usePolicy();
  const familyCustomFields = useMemo(
    () => policy.customFamilyFields ?? [],
    [policy.customFamilyFields]
  );
  const volunteerCustomFields = useMemo(
    () => policy.volunteerPolicy?.customFields ?? [],
    [policy.volunteerPolicy?.customFields]
  );
  const arrangementTypes = useMemo(
    () =>
      Array.from(
        new Set(
          (policy.referralPolicy?.arrangementPolicies ?? []).flatMap(
            (policy) => (policy.arrangementType ? [policy.arrangementType] : [])
          )
        )
      ),
    [policy.referralPolicy?.arrangementPolicies]
  );
  const roleNamesForPresentation = useMemo(
    () =>
      Array.from(
        new Set([
          ...Object.keys(policy.volunteerPolicy?.volunteerFamilyRoles ?? {}),
          ...Object.keys(policy.volunteerPolicy?.volunteerRoles ?? {}),
        ])
      ),
    [
      policy.volunteerPolicy?.volunteerFamilyRoles,
      policy.volunteerPolicy?.volunteerRoles,
    ]
  );
  const organizationNamesByFamily = useMemo(
    () =>
      organizationNamesByFamilyId(
        communities.map((communityInfo) => communityInfo.community)
      ),
    [communities]
  );
  const rows = useMemo(
    () =>
      families.flatMap((family) =>
        buildVolunteerBrowserRow(
          family,
          organizationNamesByFamily.get(family.family!.id!) ?? [],
          familyCustomFields,
          volunteerCustomFields,
          arrangementTypes,
          roleNamesForPresentation
        )
      ),
    [
      arrangementTypes,
      families,
      familyCustomFields,
      roleNamesForPresentation,
      organizationNamesByFamily,
      volunteerCustomFields,
    ]
  );
  return {
    arrangementTypes,
    familyCustomFields,
    roleNames: [notAppliedRoleFilterValue, ...roleNamesForPresentation],
    rows,
    volunteerCustomFields,
  };
}
